import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import type { Location } from '@/types/models';

export interface LocationFormData {
  name: string;
  type?: string;
  status?: string;
  site_id?: string | null;
}

// Query hooks
export function useLocations(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['locations', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name', { ascending: true });

      if (error) throw error;
      return data as Location[];
    },
    enabled: !!organizationId,
  });
}

export function useLocation(locationId: string | undefined) {
  return useQuery({
    queryKey: ['locations', locationId],
    queryFn: async () => {
      if (!locationId) throw new Error('Location ID is required');

      const { data, error } = await supabase
        .from('locations')
        .select('*, site:sites(*)')
        .eq('id', locationId)
        .single();

      if (error) throw error;
      return data as Location;
    },
    enabled: !!locationId,
  });
}

// Get locations for a specific site (sub-locations)
export function useLocationsBySite(organizationId: string | undefined, siteId: string | null | undefined) {
  return useQuery({
    queryKey: ['locations', 'bySite', organizationId, siteId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      let query = supabase
        .from('locations')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name', { ascending: true });

      if (siteId) {
        query = query.eq('site_id', siteId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Location[];
    },
    enabled: !!organizationId && !!siteId,
  });
}

// Mutation hooks
export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LocationFormData & { organization_id: string }) => {
      const { error, data: newLocation } = await supabase
        .from('locations')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return newLocation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      notifications.show({
        title: 'Success',
        message: 'Location created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create location',
        color: 'red',
      });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<LocationFormData> }) => {
      const { error, data: updatedLocation } = await supabase
        .from('locations')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return updatedLocation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      notifications.show({
        title: 'Success',
        message: 'Location updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update location',
        color: 'red',
      });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (locationId: string) => {
      const { error } = await supabase.from('locations').delete().eq('id', locationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      notifications.show({
        title: 'Success',
        message: 'Location deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete location',
        color: 'red',
      });
    },
  });
}
